import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosConfig } from "../../../axios/axiosConfig";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import SelectedTradeTab from "./SelectedTradeTab";
import { Box, Typography } from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";

import styles from "./AdvisorTradeCalls.module.css";
import { isFuturesType, getClosedTradesUrl, CANCELLED_TRADES_URL } from "./common/tradeTypes";

// Futures Pro is still being tested — visible on local/staging, hidden on production.
const isProductionEnv = process.env.REACT_APP_ENV === "production";

const tradeCallTabsArray = [
  { id: 1, label: "Cash", value: "cash" },
  //   { id: 2, label: "Options", value: "options" },
  { id: 3, label: "Futures", value: "futures" },
  ...(isProductionEnv ? [] : [{ id: 4, label: "Futures Pro", value: "future-pro" }]),
];

const AdvisorTradeCalls = () => {
  const { user } = useSelector((state) => state.auth);
  const USERID = user?.userDetails?.id;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTabFromURL = searchParams.get("activeTab");

  //   const [selectedTab, setSelectedTab] = useState(activeTabFromURL || "cash");
  const [isEditing, setIsEditing] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [cancelledTrades, setCancelledTrades] = useState([]);
  const cancelledTradesUrl = CANCELLED_TRADES_URL[activeTabFromURL];
  // Closed trades come only from the close api — the open api can also return them,
  // which showed every closed trade twice in the Closed tab.
  const CLOSED_STATUSES = ["CLOSED", "EXITED"];
  const openOnlyTrades = (openTrades || []).filter(
    (t) => !CLOSED_STATUSES.includes(t?.trade_status?.toUpperCase())
  );
  // de-dupe in case a trade comes back from more than one list api
  const allTradesList = [
    ...new Map(
      [...openOnlyTrades, ...(closedTrades || []), ...(cancelledTradesUrl ? cancelledTrades : [])].map((t) => [
        t.advice_name || t.id,
        t,
      ])
    ).values(),
  ];
  const [activeAdvisoryObj, setActiveAdvisoryObj] = useState({});
  // const exitPriceRef = useRef(null) // not working

  // Futures default to INTRADAY segment, Cash defaults to SWING
  const getInitialTradesFormInput = (tradeType) => ({
    symbolName: "",
    segment: isFuturesType(tradeType) ? "INTRADAY" : "SWING", // BTST, INTRADAY, SWING (drop down field)
    signal: "", // long or short for 'long buy, 'short sell'
    entryPrice: "", // suggested price, NO RUPEE symbols in entry, target and stop loss
    targetPrice: "",
    stopLoss: "",
    // description: "",
    exitPrice: "N/A", // EXIT price is only visible for EDIT / UPDATE
    lotSize: "", // 150.0 - FOR FUTURES ONLY
    lotExpiry: "", // "26 dec"  - FOR FUTURES ONLY
    profitLoss: "",
    tradeClosedInDays: "",
    trade_status: "OPEN",
    cancel_reason:"",
    call_type: isFuturesType(tradeType) ? "CMP" : "BID",
    bid_type: "",
    ltp: "",
    valid_for_parrva: false,
    parrva_call_type: !isFuturesType(tradeType) ? "TECHNICAL" : "",
    stock_horizon: "",
    instrument: isFuturesType(tradeType) ? "STOCK_FUTURE" : "",
    entry_price_range: isFuturesType(tradeType) ? "CMP" : "",
    trade_type: tradeType === "future-pro" ? "Pro" : "",
  });
  const [tradesFormInput, setTradesFormInput] = useState(
    getInitialTradesFormInput(activeTabFromURL)
  );
  // CLEAR / RESET advisoryFormInput
  const clearTradesFormInput = () => {
    setTradesFormInput(getInitialTradesFormInput(activeTabFromURL));
  };

  // Handle Create Trade button - opens modal with create mode
  const handleCreateTradeBtn = () => {
    clearTradesFormInput();
    setIsEditing(false);
    setActiveAdvisoryObj({});
    setIsFormModalOpen(true);
  };

  // FETCHING OPEN TRADE CALLS, (cash, options, and futures)
  const openTradeCallsQuery = useQuery({
    retry: false,
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchInterval: false, // Disable automatic refetching
    queryKey: ["open-trades", activeTabFromURL], // dependency
    queryFn: () => fetchOpenTrades(USERID, activeTabFromURL),
    onSuccess: (data) => {
      //   setAllTradesList(data.response?.data);
      setOpenTrades(data.response?.data);
    },
    onError: (error) => {
      // console.log(" error: ", error);
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  const refreshTrades = () => {
  openTradeCallsQuery.refetch();
  closeTradeCallsQuery.refetch();
  if (cancelledTradesUrl) cancelledTradeCallsQuery.refetch();
};

  // FETCHING OPEN TRADE CALLS, (cash, options, and futures)
  const closeTradeCallsQuery = useQuery({
    retry: false,
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchInterval: false, // Disable automatic refetching
    queryKey: ["closed-trades", activeTabFromURL], // dependency
    queryFn: () => fetchClosedTrades(USERID, activeTabFromURL),
    onSuccess: (data) => {
      setClosedTrades(data.response?.data);
    },
    onError: (error) => {
      // console.log(" error: ", error);
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  // FETCHING CANCELLED TRADE CALLS (Future Pro only)
  const cancelledTradeCallsQuery = useQuery({
    enabled: !!cancelledTradesUrl,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    queryKey: ["cancelled-trades", activeTabFromURL],
    queryFn: () => fetchTradesList(cancelledTradesUrl),
    onSuccess: (data) => {
      setCancelledTrades(data.response?.data || []);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  // this function copies the object row in tradesTable to tradesFormInput, makes it active
  // Edit mode: only allows editing stopLoss and targetPrice
  const handleEditBtn = (item_obj) => {
    const { symbol_name, segment, entry_price, target_price, stop_loss } =
      item_obj;
    const tempTradesFormInput = {
      symbolName: symbol_name,
      segment: segment,
      signal: item_obj.signal,
      entryPrice: entry_price,
      targetPrice: target_price,
      stopLoss: stop_loss,
      lotSize: item_obj?.lot_size,
      lotExpiry: item_obj?.lot_expiry,
      trade_status: item_obj?.trade_status || "OPEN",
      cancel_reason: item_obj?.cancel_reason || "",
      exitPrice: item_obj?.exit_price || "",
      action_type: "edit",
      ltp: "",
      instrument: item_obj?.instrument || "",
      entry_price_range: item_obj?.entry_price_range || "",
      call_type: item_obj?.call_type || "BID",
      bid_type: item_obj?.bid_type || "",
      valid_for_parrva: !!item_obj?.valid_for_parrva,
      parrva_call_type: item_obj?.parrva_call_type || "",
      stock_horizon: item_obj?.stock_horizon || "",
      trade_type: item_obj?.trade_type || "",
    };

    setIsEditing(true);
    setActiveAdvisoryObj(item_obj);
    setTradesFormInput(tempTradesFormInput);
    setIsFormModalOpen(true);
  };

  // Handle Cancel button - opens form with cancel mode
  const handleCancelBtn = (item_obj) => {
    const tempTradesFormInput = {
      symbolName: item_obj.symbol_name,
      segment: item_obj.segment,
      signal: item_obj.signal,
      entryPrice: item_obj.entry_price,
      targetPrice: item_obj.target_price,
      stopLoss: item_obj.stop_loss,
      lotSize: item_obj?.lot_size,
      lotExpiry: item_obj?.lot_expiry,
      exitPrice: item_obj?.exit_price || "N/A",
      trade_status: "CANCELLED",
      cancel_reason: "",
      call_type: item_obj?.call_type || "BID",
      action_type: "cancel",
      ltp: "",
    };

    setIsEditing(true);
    setActiveAdvisoryObj(item_obj);
    setTradesFormInput(tempTradesFormInput);
    setIsFormModalOpen(true);
  };

  // Handle Exit button - opens form with exit mode
  const handleExitBtn = (item_obj) => {
    const tempTradesFormInput = {
      symbolName: item_obj.symbol_name,
      segment: item_obj.segment,
      signal: item_obj.signal,
      entryPrice: item_obj.entry_price,
      targetPrice: item_obj.target_price,
      stopLoss: item_obj.stop_loss,
      lotSize: item_obj?.lot_size,
      lotExpiry: item_obj?.lot_expiry,
      exitPrice: "",
      trade_status: "EXITED",
      cancel_reason: "",
      call_type: item_obj?.call_type || "BID",
      action_type: "exit",
      ltp: "",
    };

    setIsEditing(true);
    setActiveAdvisoryObj(item_obj);
    setTradesFormInput(tempTradesFormInput);
    setIsFormModalOpen(true);
  };

  // Close modal handler
  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setIsEditing(false);
    clearTradesFormInput();
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3,
          py: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ShowChartIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              Trades (Cash, Futures &amp; Futures Pro)
            </Typography>
            <Typography sx={{ color: "#b8d4c4", fontSize: 12 }}>
              Manage and track trade calls
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {/* Cash / Futures / Future Pro tabs */}
          <Box sx={{ display: "flex", gap: 0 }}>
            {tradeCallTabsArray.map((item, i) => (
              <button
                key={item.id}
                onClick={() => navigate(`/advisor/trade-calls?activeTab=${item.value}`)}
                style={{
                  padding: "8px 22px",
                  cursor: item.value === "options" ? "not-allowed" : "pointer",
                  backgroundColor: item.value === activeTabFromURL ? "#016551" : "rgba(255,255,255,0.12)",
                  color: "#fff",
                  border: "none",
                  borderRadius: i === 0 ? "6px 0 0 6px" : i === tradeCallTabsArray.length - 1 ? "0 6px 6px 0" : 0,
                  fontWeight: item.value === activeTabFromURL ? 700 : 400,
                  fontSize: 13,
                  opacity: item.value === "options" ? 0.4 : 1,
                  transition: "background 0.2s",
                }}
              >
                {item.label}
              </button>
            ))}
          </Box>

          {/* Create Trade Button */}
          <button
            onClick={handleCreateTradeBtn}
            style={{
              padding: "9px 22px",
              background: "#016551",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "14px",
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              transition: "background 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "linear-gradient(135deg, #1db954 0%, #17a349 100%)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(1,101,81,0.4)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#016551"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(1,101,81,0.3)"; }}
          >
            + Create Trade
          </button>
        </Box>
      </Box>

      <SelectedTradeTab
        selectedTradeType={activeTabFromURL}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        tradesFormInput={tradesFormInput}
        setTradesFormInput={setTradesFormInput}
        clearTradesFormInput={clearTradesFormInput}
        activeAdvisoryObj={activeAdvisoryObj}
        allTradesList={allTradesList}
        handleEditBtn={handleEditBtn}
        handleCancelBtn={handleCancelBtn}
        handleExitBtn={handleExitBtn}
        isFormModalOpen={isFormModalOpen}
        setIsFormModalOpen={setIsFormModalOpen}
        handleCloseFormModal={handleCloseFormModal}
        refreshTrades={refreshTrades}
      />
    </Box>
  );
};

export default AdvisorTradeCalls;

// UTILITIES
// fetching all trades (cash, futures, options) (open or closed)
const fetchOpenTrades = async (user_id, selected_trade_type) => {
  // if (!user_id) return;

  const res = await axiosConfig({
    method: "get",
    // url: "/predictions/stocks/advisory?status=OPEN",
    url: `/predictions/call/${user_id}/${selected_trade_type}`,
    data: {},
  });

  return res.data;
};

const fetchTradesList = async (url) => {
  const res = await axiosConfig({ method: "get", url, data: {} });
  return res.data;
};

const fetchClosedTrades = async (user_id, selected_trade_type) => {
  // if (!user_id) return;

  const res = await axiosConfig({
    method: "get",
    // url: "/predictions/stocks/advisory?status=OPEN",
    // url: `/predictions/call/${user_id}/${selected_trade_type}`,
    url: getClosedTradesUrl(selected_trade_type),
    data: {},
  });

  return res.data;
};